import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Spinner } from '@nextui-org/react';
import { Search, User as UserIcon } from 'lucide-react';
import statsExplorerService, { StatsSearchPlayer } from '../../services/statsExplorerService';

const TEAL = '#3fced1';
const DEBOUNCE_MS = 350;

interface PlayerSearchBoxProps {
  /** Initiale Suchanfrage (z.B. aus ?q= URL-Parameter) */
  initialQuery?: string;
  placeholder?: string;
  size?: 'md' | 'lg';
  autoFocus?: boolean;
}

/**
 * Spieler-Suche mit Debounce (350ms) und Dropdown-Ergebnissen.
 * Klick auf ein Ergebnis navigiert zu /statistiken/spieler/:flashliveId.
 */
const PlayerSearchBox: React.FC<PlayerSearchBoxProps> = ({
  initialQuery = '',
  placeholder = 'Spieler suchen, z.B. "Littler" oder "van Gerwen"...',
  size = 'lg',
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<StatsSearchPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestQueryRef = useRef('');

  const runSearch = useCallback(async (q: string) => {
    latestQueryRef.current = q;
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      return;
    }
    setLoading(true);
    const players = await statsExplorerService.searchPlayers(q, 20);
    // Stale Responses ignorieren
    if (latestQueryRef.current !== q) return;
    setResults(players);
    setLoading(false);
    setSearched(true);
    setOpen(true);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  // Initial query direkt ausführen
  useEffect(() => {
    if (initialQuery && initialQuery.trim().length >= 2) {
      runSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (player: StatsSearchPlayer) => {
    const id = player.flashliveId;
    if (!id) return;
    setOpen(false);
    navigate(`/statistiken/spieler/${encodeURIComponent(id)}`);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        value={query}
        onValueChange={setQuery}
        onFocus={() => {
          if (results.length > 0 || searched) setOpen(true);
        }}
        placeholder={placeholder}
        size={size}
        autoFocus={autoFocus}
        startContent={<Search size={size === 'lg' ? 22 : 18} className="text-gray-500" />}
        endContent={loading ? <Spinner size="sm" style={{ color: TEAL }} /> : null}
        classNames={{
          inputWrapper:
            'bg-zinc-900 border border-zinc-800 hover:border-zinc-700 data-[focus=true]:border-[#3fced1]/60',
          input: 'text-white placeholder:text-gray-600',
        }}
        aria-label="Spieler suchen"
      />

      {open && (query.trim().length >= 2) && (
        <div className="absolute z-50 mt-2 w-full bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl shadow-black/60 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="sm" style={{ color: TEAL }} />
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-5 text-center">
              <UserIcon size={24} className="mx-auto text-gray-600 mb-1" />
              <p className="text-sm text-gray-500">Keine Spieler gefunden</p>
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-zinc-800/60">
              {results.map((player, idx) => {
                const name = statsExplorerService.cleanPlayerName(player.name || player.shortName);
                const countryName = statsExplorerService.getCountryName(player.country);
                const flag = statsExplorerService.getFlagEmoji(countryName);
                return (
                  <li key={player.flashliveId || player._id || idx}>
                    <button
                      onClick={() => handleSelect(player)}
                      disabled={!player.flashliveId}
                      className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-800/60 transition-colors disabled:opacity-50"
                    >
                      {player.image ? (
                        <img
                          src={player.image}
                          alt={name}
                          className="w-9 h-9 rounded-full object-cover bg-zinc-800 flex-shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <UserIcon size={16} className="text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {flag && <span className="mr-1.5">{flag}</span>}
                          {name}
                        </p>
                        {countryName && (
                          <p className="text-xs text-gray-500 truncate">{countryName}</p>
                        )}
                      </div>
                      {typeof player.currentRanking === 'number' && player.currentRanking > 0 && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: `${TEAL}1f`, color: TEAL }}
                        >
                          #{player.currentRanking}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerSearchBox;
